import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { config } from '../config/environment';

export interface SubscriptionData {
  hasSubscription: boolean;
  plano: string | null;
  token: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}

class SubscriptionService {
  private firestoreEnabled = true;

  constructor() {
    // Limpar localStorage de tokens falsos na inicialização
    this.clearLocalStorageTokens();
  }

  private clearLocalStorageTokens(): void {
    try {
      const keys = Object.keys(localStorage);
      const loopraKeys = keys.filter(key => key.startsWith('loopra_subscription_'));
      loopraKeys.forEach(key => {
        console.log('[DEBUG] Removendo token local falso:', key);
        localStorage.removeItem(key);
      });
      if (loopraKeys.length > 0) {
        console.log('[DEBUG] Limpeza completa: removidos', loopraKeys.length, 'tokens locais');
      }
    } catch (error) {
      console.warn('[WARN] Erro ao limpar localStorage:', error);
    }
  }

  async hasSubscription(googleId: string): Promise<SubscriptionData> {
    try {
      console.log('[DEBUG] SubscriptionService - Verificando assinatura para:', googleId);
      console.log('[DEBUG] Usando APENAS API backend:', config.apiUrl);

      // SEMPRE usa API do Spring Boot em TODOS os ambientes
      return await this.checkApiSubscription(googleId);
    } catch (error) {
      console.error('[ERROR] SubscriptionService - Erro ao verificar assinatura:', error);
      return {
        hasSubscription: false,
        plano: null,
        token: null
      };
    }
  }

  async subscribe(googleId: string, userEmail?: string, userName?: string, planType: string = 'mensal'): Promise<{ plano: string; token: string; success: boolean }> {
    try {
      console.log('[DEBUG] SubscriptionService - Criando assinatura para:', googleId);
      console.log('[DEBUG] Plano:', planType);
      console.log('[DEBUG] Usando APENAS API backend:', config.apiUrl);

      // SEMPRE usa API do Spring Boot em TODOS os ambientes
      return await this.createApiSubscription(googleId, userEmail, userName, planType);
    } catch (error) {
      console.error('[ERROR] SubscriptionService - Erro ao criar assinatura:', error);
      throw error;
    }
  }



  private async checkApiSubscription(googleId: string): Promise<SubscriptionData> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
  const response = await fetch(`${config.apiUrl}/api/user/hasSubscription?googleId=${googleId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        signal: controller.signal,
        mode: 'cors',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[DEBUG] API Response:', data);

      return {
        hasSubscription: data.hasSubscription || false,
        plano: data.plano || null,
        token: data.token || null
      };
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('API timeout');
      }
      throw error;
    }
  }

  private async createApiSubscription(googleId: string, userEmail?: string, userName?: string, planType: string = 'mensal'): Promise<{ plano: string; token: string; success: boolean }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
  const response = await fetch(`${config.apiUrl}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          googleId,
          userEmail: userEmail || '',
          userName: userName || '',
          planType: planType
        }),
        signal: controller.signal,
        mode: 'cors',
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[DEBUG] API Subscribe Response:', data);

      return {
        plano: data.plano || 'Enterprise',
        token: data.token || '',
        success: true
      };
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('API timeout');
      }
      throw error;
    }
  }

}

export const subscriptionService = new SubscriptionService();